const STORAGE_PREFIX = "DataTableColumnsState";
const STORAGE_VERSION = 1;

const toPositiveWidth = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const buildStateFromColumns = (columns = []) => ({
  order: columns.map((column) => column.key),
  widths: columns.reduce((acc, column) => {
    const width = toPositiveWidth(column?.width);
    if (width) {
      acc[column.key] = width;
    }
    return acc;
  }, {}),
});

const resolveTableIdentity = ({
  id,
  dataTestId,
  className,
  rowKey,
}) => {
  const pathname =
    typeof window !== "undefined"
      ? window.location?.pathname || "unknown-route"
      : null;

  if (id) return `id:${id}`;
  if (dataTestId) return `testid:${dataTestId}`;
  if (className) return `class:${String(className).split(" ")[0]}`;
  if (typeof rowKey === "string") return `rowkey:${rowKey}`;
  if (pathname) return `route:${pathname}`;
  return "anonymous";
};

export const getDataTableStorageKey = ({ tableIdentity, columns = [] }) => {
  const columnKeys = columns.map((column) => column?.key).filter(Boolean);

  if (!Array.isArray(columnKeys)) {
    return null;
  }

  const resolvedTableIdentity = resolveTableIdentity(tableIdentity || {});
  const columnsSignature = columnKeys.join("|");
  const composedKey = `${resolvedTableIdentity}::${columnsSignature}`;
  const encodedKey = encodeURIComponent(composedKey);

  return `${STORAGE_PREFIX}:${encodedKey}`;
};

const normalizePersistedState = (rawValue) => {
  if (!rawValue) {
    return null;
  }

  if (
    rawValue?.version === STORAGE_VERSION &&
    Array.isArray(rawValue?.order) &&
    typeof rawValue?.widths === "object" &&
    rawValue?.widths !== null
  ) {
    return {
      order: rawValue.order,
      widths: rawValue.widths,
    };
  }

  return null;
};

export const readPersistedColumnsState = (storageKey) => {
  if (!storageKey || typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    return normalizePersistedState(rawValue ? JSON.parse(rawValue) : null);
  } catch (_error) {
    return null;
  }
};

export const reconcileColumnsWithState = (columns, state) => {
  const nextByKey = new Map(columns.map((column) => [column.key, column]));
  const persistedOrder = (state?.order || []).filter((key) => nextByKey.has(key));
  const missingKeys = columns
    .map((column) => column.key)
    .filter((key) => !persistedOrder.includes(key));
  const mergedOrder = [...persistedOrder, ...missingKeys];

  return mergedOrder.map((key) => {
    const column = nextByKey.get(key);
    const persistedWidth = toPositiveWidth(state?.widths?.[key]);
    return {
      ...column,
      width: persistedWidth || column?.width,
    };
  });
};

export const writePersistedColumnsState = (storageKey, columns) => {
  if (!storageKey || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        version: STORAGE_VERSION,
        ...buildStateFromColumns(columns),
      })
    );
  } catch (_error) {
    // Ignore storage write failures (quota/private mode/security).
  }
};

export const getInMemoryColumnsState = buildStateFromColumns;
